import Text from 'admin/components/common/Text';
import { staticCollectionFieldsMap } from 'admin/components/pages/Collections/CollectionEditor/CollectionTable/utils';
import { COLLECTION_PAGE, COLLECTIONS_PAGE } from 'admin/constants/routes';
import { PARAMS } from 'common/constants';
import { UNIFORM_PROPS } from 'common/constants/component-props';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Steps, ConfigProvider, ThemeConfig, Flex } from 'antd';
import { Link } from 'react-router-dom';
import { CmsCollectionType } from 'types/cms';
import cmsUtils from 'common/utils/cms';
import { MODAL } from 'admin/constants/common';
import { useAdminSelector } from 'admin/reducers';
import Button from 'admin/components/common/Button';
import { useListCollectionItems } from 'admin/queries/cms/list-collection-items';
import { Hint } from 'admin/components/common/Form/Hint';
import { Select } from 'admin/components/common/Form/Select';
import { ModalBody, ModalFooter } from 'admin/components/common/Modal';
import { selectCollections } from 'admin/reducers/cms/selectors';
import type { ModalManagerProvidedProps } from 'admin/components/common/ModalManager';
import { Context as ThemeContext } from 'admin/components/common/Theme';
import { Column, Grid } from 'admin/components/common/Grid';
import { generatePath } from 'utils/route';
import { getCollectionFieldOptions } from '../utils';
import css from './CmsPropBindModalData.scss';

const texts = {
	linkCollectionItemHint:
		'To finish setup:' +
		' \n1. Open Link section in sidebar' +
		' \n2. Select destination card/URL' +
		' \n3. We will automatically add item ID parameter to the URL',
	byRouteIdHint:
		`Use a specific collection item data by adding '?${PARAMS.COLLECTION_ITEM_ID}=your-id' to your page’s URL. ` +
		'\n\n If you don’t, we’ll just show the first item from your collection.',
	byContextIdHint:
		'To automatically display data from your collection:' +
		"\n\n1. Find a 'CMS Repeater' component in Layers" +
		'\n2. Place this component inside that repeater' +
		"\n\nNote: If you don't set this up, the first item from your collection will be shown.",
	byRouteIdLabel: 'From URL (use item ID in URL)',
	byContextIdLabel: 'From CMS Repeater Context',
	byContextOption: (collection: CmsCollectionType) => `${collection.name}  (Repeater context)`,
	componentProperty: 'Component property',
	collectionTitle: 'Collection',
	collectionPlaceholder: 'Select collection',
	collectionDataTitle: 'Collection data',
	collectionColumnLabel: 'Column',
	collectionDataLabel: 'Data',
	collectionIdDataOptionLabel: 'Collection item ID',
	viewCollectionText: 'Want a quick look?\t',
	viewCollectionLink: 'Open collection page',
	noCollection: {
		atAll: {
			text: "No collections found — let's get started!\n",
			link: 'Click here to create one',
		},
		selected: {
			text: 'Choose a collection from the list or ',
			link: 'create a new one',
		},
	},
};

enum StepIndex {
	PROPERTY,
	COLLECTION,
	DATA,
}

const collectionDataIdOptions = [
	{ label: texts.byContextIdLabel, value: cmsUtils.DataIdSource.BY_CONTEXT },
	{ label: texts.byRouteIdLabel, value: cmsUtils.DataIdSource.BY_ROUTE },
];

const antdTheme: ThemeConfig = {
	token: {
		colorText: '#fff',
		colorTextDescription: 'rgba(255, 255, 255, 0.45)',
		colorTextDisabled: 'blue',
		colorTextQuaternary: 'red',
		colorBorderSecondary: 'rgba(255, 255, 255, 0.18)',
	},
};

const maxMenuHeight = 200;

type ContentProps = ModalManagerProvidedProps<MODAL.CMS_PROP_BIND_MODAL>;

const NoCollection = ({ isFirst = false }: { isFirst?: boolean }) => (
	<Text size={isFirst ? 'article' : 'description'}>
		{isFirst ? texts.noCollection.atAll.text : texts.noCollection.selected.text}
		<Link target="_blank" to={generatePath(COLLECTIONS_PAGE, {})}>
			{isFirst ? texts.noCollection.atAll.link : texts.noCollection.selected.link}
		</Link>
	</Text>
);

const ModalContent: React.FC<ContentProps> = props => {
	const { theme } = useContext(ThemeContext);
	const cmsCollections = useAdminSelector(selectCollections);
	const [currentStep, setCurrentStep] = useState(props.data.view === 'edit' ? StepIndex.DATA : StepIndex.PROPERTY);
	const [propOptions] = useState(props.data.properties.map(p => ({ value: p.propertyKey, label: p.displayName })));
	const [property, setProperty] = useState(propOptions[0].value);
	const [collectionId = '', setCollectionId] = useState(props.data.collectionId);

	// Fetch collection items for the selected collection
	useListCollectionItems({ collectionId, pageSize: cmsUtils.defaultProps.collectionPageSize });

	const { dataKey, setDataKey, dataKeyOptions } = useDataKey({
		collectionId,
		data: props.data,
		property,
		cmsCollections,
	});
	const [defaultDataId] = useState(() => {
		if (props.data.view === 'edit') {
			return props.data.dataId;
		}

		return cmsUtils.DataIdSource.BY_CONTEXT;
	});
	const [dataId, setDataId] = useState(defaultDataId);

	const onInputChange = (value: string, callback: (v: string) => void) => callback(value);

	const collectionOptions = React.useMemo(() => {
		const getCustomLabel = (_collection: CmsCollectionType): string | null => {
			if (_collection.id === props.data.repeatableCollectionId) return texts.byContextOption(_collection);
			return null;
		};

		const sortByRepeatableAndName = (a: CmsCollectionType, b: CmsCollectionType): number => {
			if (a.id === props.data.repeatableCollectionId) return -1;
			if (b.id === props.data.repeatableCollectionId) return 1;
			return a.name.localeCompare(b.name);
		};

		return (
			cmsCollections?.sort(sortByRepeatableAndName).map(_collection => ({
				value: _collection.id,
				label: getCustomLabel(_collection) || _collection.name,
			})) ?? []
		);
	}, [cmsCollections, props.data.repeatableCollectionId]);

	const steps = [
		{
			title: texts.componentProperty,
			description: propOptions.find(p => p.value === property)?.label,
			isValid: !!property,
			content: (
				<Select
					options={propOptions}
					value={property}
					theme={theme}
					maxMenuHeight={maxMenuHeight}
					eventListeners={{ onChange: v => onInputChange(v, setProperty) }}
				/>
			),
		},
		{
			title: texts.collectionTitle,
			description: collectionOptions.find(p => p.value === collectionId)?.label,
			isValid: !!property && !!collectionId,
			content: (
				<Flex vertical gap="small">
					<Select
						options={collectionOptions}
						value={collectionId}
						theme={theme}
						maxMenuHeight={maxMenuHeight}
						isSearchable
						placeholder={texts.collectionPlaceholder}
						eventListeners={{
							onChange: v =>
								onInputChange(v, async nextCollectionId => {
									setCollectionId(nextCollectionId);
								}),
						}}
					/>

					{collectionId ? (
						<Text size="description">
							{texts.viewCollectionText}
							<Link target="_blank" to={generatePath(COLLECTION_PAGE, { collectionId })}>
								{texts.viewCollectionLink}
							</Link>
						</Text>
					) : (
						<NoCollection />
					)}
				</Flex>
			),
		},
		{
			title: texts.collectionDataTitle,
			description: [
				dataKeyOptions.find(p => p.value === dataKey)?.label,
				collectionDataIdOptions.find(p => p.value === dataId)?.label,
			]
				.filter(v => !!v)
				.join(' / '),
			isValid: !!property && !!collectionId && !!dataKey,
			content: (
				<>
					<Select
						label={{ children: texts.collectionColumnLabel }}
						options={dataKeyOptions}
						value={dataKey}
						theme={theme}
						maxMenuHeight={maxMenuHeight}
						isSearchable
						eventListeners={{ onChange: v => onInputChange(v, setDataKey) }}
					/>
					{property === UNIFORM_PROPS.btnCollectionLink &&
						dataKey === staticCollectionFieldsMap.get('id')?.key && (
							<Hint theme={theme} text={texts.linkCollectionItemHint} className={css.hint} />
						)}
					<br />
					<br />
					<Select
						label={{ children: texts.collectionDataLabel }}
						options={collectionDataIdOptions}
						value={dataId}
						theme={theme}
						eventListeners={{ onChange: setDataId }}
						maxMenuHeight={maxMenuHeight}
						placeholder={defaultDataId}
					/>

					{dataId === cmsUtils.DataIdSource.BY_ROUTE && (
						<Hint className={css.hint} text={texts.byRouteIdHint} theme={theme} />
					)}

					{dataId === cmsUtils.DataIdSource.BY_CONTEXT && (
						<Hint className={css.hint} text={texts.byContextIdHint} theme={theme} />
					)}
				</>
			),
		},
	];

	const lastStepIndex = steps.length - 1;

	const isSaveDisabled = !collectionId || !dataKey || !property;

	const handleSave = () => {
		props.data.onSave({
			property,
			collectionId: collectionId!,
			dataKey: dataKey!,
			dataId,
		});
		props.close();
	};

	if (collectionOptions.length === 0) {
		return (
			<>
				<ModalBody>
					<NoCollection isFirst />
				</ModalBody>
				<ModalFooter>
					<Button onClick={props.close}>Cancel</Button>
				</ModalFooter>
			</>
		);
	}

	return (
		<>
			<ModalBody>
				<Grid columns="14" gap="large">
					<Column colSpan="6">{steps[currentStep].content}</Column>

					<Column colSpan="8" style={{ paddingTop: 16 }}>
						<ConfigProvider theme={antdTheme}>
							<Steps
								className={css.steps}
								size="small"
								orientation="vertical"
								current={currentStep}
								items={steps.map(step => ({
									key: `step-${step.title}`,
									title: step.title,
									content: step.isValid ? step.description : undefined,
									disabled: !step.isValid,
								}))}
								onChange={setCurrentStep}
							/>
						</ConfigProvider>
					</Column>
				</Grid>
			</ModalBody>

			<ModalFooter>
				{currentStep < lastStepIndex ? (
					<Button
						view="secondary"
						color="primary"
						disabled={steps[currentStep].isValid === false}
						onClick={() => setCurrentStep(s => s + 1)}
					>
						Next
					</Button>
				) : (
					<Button view="primary" disabled={isSaveDisabled} onClick={handleSave}>
						Save
					</Button>
				)}

				<Button view="secondary-gray" onClick={props.close}>
					Cancel
				</Button>
			</ModalFooter>
		</>
	);
};

interface UseDataKeyOptionsProps {
	property: string;
	data: ContentProps['data'];
	cmsCollections: CmsCollectionType[];
	collectionId: CmsCollectionType['id'];
}

function useDataKey({ collectionId, property, data, cmsCollections }: UseDataKeyOptionsProps) {
	const dataKeyOptions = useMemo(() => {
		const result = getCollectionFieldOptions(
			cmsCollections?.find(c => c.id === collectionId)?.fields ?? [],
			data.properties.find(p => p.propertyKey === property)?.allowedFieldTypes ?? [],
			['position']
		);

		// The only case when we need to add internal collection item ID to the list of options
		// is when the property is a link to a collection item
		if (property === UNIFORM_PROPS.btnCollectionLink) {
			result.push({
				label: texts.collectionIdDataOptionLabel,
				value: staticCollectionFieldsMap.get('id')!.key,
			});
		}

		return result;
	}, [property, data, cmsCollections, collectionId]);

	const [dataKey, setDataKey] = useState<string | undefined>();

	useEffect(() => {
		const defaultDataKey = data.view === 'edit' ? data.dataKey : dataKeyOptions[0]?.value;

		const matched = dataKeyOptions.find(opt => opt.value === defaultDataKey)?.value;
		setDataKey(matched);
	}, [dataKeyOptions, data]);

	return {
		dataKeyOptions,
		dataKey,
		setDataKey,
	};
}

export default ModalContent;
